import Card from 'components/card';
import { MdDelete, MdPlayArrow, MdCalendarToday, MdFactory, MdLocationOn, MdQrCode, MdComment, MdEvent } from 'react-icons/md';
import { dateToFrench } from 'utils/date';
import { getIcon } from 'utils/icons';

const PlannedProductionItem = (props: {
    production: any;
    config?: any;
    onView: () => void;
    onDelete: () => void;
}) => {
    const { production, config, onView, onDelete } = props;
    const isProductNotFound = production.productName === 'Product not found';
    
    // function to get the label of a custom field
    const getFieldLabel = (fieldKey: string): string => {
        if (!config || !config.customFields) return fieldKey;
        const field = config.customFields.find((f: any) => f.key === fieldKey);
        return field ? field.label : fieldKey;
    };

    const getStatusBadge = () => {
        switch (production.status) {
            case 'scheduled':
                return {
                    color: 'green',
                    label: 'PLANIFIÉ',
                    classes: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
                    dotColor: 'bg-green-500',
                    description: 'En attente de démarrage'
                };
            case 'custom_step':
                const stepName = production.customStepName || 'Étape personnalisée';
                return {
                    color: 'blue',
                    label: `EN ATTENTE: ${stepName.toUpperCase()}`,
                    classes: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
                    dotColor: 'bg-blue-500',
                    description: `En cours - Étape ${production.currentStepIndex || 1}`
                };
            case 'pending_validation':
                return {
                    color: 'orange',
                    label: 'EN ATTENTE: VALIDATION',
                    classes: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
                    dotColor: 'bg-orange-500',
                    description: 'Nécessite validation avant mise en stock'
                };
            default:
                return {
                    color: 'gray',
                    label: 'INCONNU',
                    classes: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400',
                    dotColor: 'bg-gray-500',
                    description: 'Statut inconnu'
                };
        }
    };

    const statusBadge = getStatusBadge();

    return (
        <Card extra="group w-full overflow-hidden transition-all duration-200 hover:shadow-lg border-l-4 border-l-toola-500">
            <div className="relative p-4">
                {/* Status badge */}
                <div className="absolute top-3 right-3 z-0">
                    <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 ${statusBadge.classes}`}>
                        <div className={`h-2 w-2 animate-pulse rounded-full ${statusBadge.dotColor}`}></div>
                        <span className="text-xs font-bold">{statusBadge.label}</span>
                    </div>
                </div>

                <div className="flex flex-col gap-4">
                    {/* Image and main info section */}
                    <div className="flex items-start gap-3">
                        <div className="relative">
                            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl bg-gray-50 dark:bg-navy-700">
                                <img
                                    className="h-14 w-14 rounded-lg object-contain"
                                    src={getIcon(production.productName, 'product')}
                                    alt={production.productName}
                                />
                            </div>
                            {/* Quantity badge */}
                            <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-toola-500 text-xs font-bold text-white">
                                {production.quantity}
                            </div>
                        </div>

                        <div className="flex-1 min-w-0 pr-32">
                            <h5 className={`text-base font-bold leading-tight ${
                                isProductNotFound 
                                    ? 'text-red-600 dark:text-red-400' 
                                    : 'text-navy-700 dark:text-white'
                            }`}>
                                {isProductNotFound
                                    ? "⚠️ Le produit n'existe plus"
                                    : production.productName}
                            </h5>
                            
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {statusBadge.description}
                            </p>
                        </div>
                    </div>

                    {/* Details grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-navy-800">
                            <MdFactory className="h-4 w-4 text-toola-500 flex-shrink-0" />
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Quantité</p>
                                <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                    {production.quantity} {production.quantity > 1 ? 'unités' : 'unité'}
                                </p>
                            </div>
                        </div>
                        
                        {production.date && (
                            <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-navy-800">
                                <MdCalendarToday className="h-4 w-4 text-toola-500 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Date prévue</p>
                                    <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                        {dateToFrench(production.date)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {production.area && (
                            <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-navy-800">
                                <MdLocationOn className="h-4 w-4 text-toola-500 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Zone</p>
                                    <p className="text-sm font-semibold text-navy-700 dark:text-white uppercase">
                                        {production.area}
                                    </p>
                                </div>
                            </div>
                        )}

                        {production.lotNumber && (
                            <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-navy-800">
                                <MdQrCode className="h-4 w-4 text-toola-500 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">N° de lot</p>
                                    <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                        {production.lotNumber}
                                    </p>
                                </div>
                            </div>
                        )}

                        {production.DLC && (
                            <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-navy-800">
                                <MdEvent className="h-4 w-4 text-toola-500 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">DLC</p>
                                    <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                        {dateToFrench(production.DLC)}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Comment section */}
                    {production.comment && (
                        <div className="rounded-lg bg-blue-50 p-3 dark:bg-navy-800">
                            <div className="flex items-start gap-2">
                                <MdComment className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                                        Commentaire
                                    </p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                        {production.comment}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Ingredients section */}
                    {production.ingredients && production.ingredients.length > 0 && (
                        <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-800">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                Ingrédients ({production.ingredients.length})
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {production.ingredients.slice(0, 5).map((ing: any, idx: number) => (
                                    <span 
                                        key={idx}
                                        className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:ring-gray-600"
                                    >
                                        {ing.name} ({ing.quantity})
                                    </span>
                                ))}
                                {production.ingredients.length > 5 && (
                                    <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-navy-700 dark:text-gray-400">
                                        +{production.ingredients.length - 5} autres
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Custom data from workflow steps */}
                    {production.customData && Object.keys(production.customData).length > 0 && (
                        <div className="rounded-lg bg-purple-50 p-3 dark:bg-navy-800">
                            <p className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-2">
                                Données de production
                            </p>
                            <div className="space-y-1.5">
                                {Object.entries(production.customData).map(([key, value]: [string, any]) => (
                                    <div key={key} className="flex items-center justify-between text-sm rounded-lg bg-white/50 dark:bg-navy-700/50 p-2">
                                        <span className="text-gray-600 dark:text-gray-400 font-medium">
                                            {getFieldLabel(key)}:
                                        </span>
                                        <span className="font-semibold text-navy-700 dark:text-white ml-2">
                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t dark:border-gray-700">
                        <button
                            onClick={onView}
                            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-toola-500 px-3 py-2.5 font-medium transition-all duration-200 hover:bg-toola-600 shadow-sm hover:shadow-md text-white"
                        >
                            <MdPlayArrow className="h-5 w-5 text-white" />
                            <span className="text-sm font-semibold text-white">
                                {production.status === 'scheduled' ? 'Démarrer' : 'Continuer'}
                            </span>
                        </button>
                        <button
                            onClick={onDelete}
                            className="flex items-center justify-center gap-2 rounded-lg bg-red-50 px-3 py-2.5 font-medium text-red-600 transition-all duration-200 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                        >
                            <MdDelete className="h-5 w-5" />
                            <span className="hidden sm:inline text-sm font-semibold">Supprimer</span>
                        </button>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default PlannedProductionItem;
